import React, { useState } from "react";
import { X, Send, Sparkles, Loader2, Check } from "lucide-react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { CLINIC_PHONE } from "./Header";

export default function AIChat({ open, onClose }) {
  const [data, setData] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!data.message.trim()) return;
    setSubmitting(true);
    try {
      await emailjs.send(
        "YOUR_SERVICE_ID",
        "YOUR_TEMPLATE_ID",
        {
          ...data,
          subject: "New Message from AI Chat",
        },
        "YOUR_PUBLIC_KEY"
      );
      setDone(true);
      toast.success("Message sent!");
      const waText = encodeURIComponent(
        `Hi Smile Savers Dental Clinic!\n\nI just sent a message from the website chat:\n• Name: ${data.name || 'N/A'}\n• Email: ${data.email || 'N/A'}\n\nMessage: ${data.message}`
      );
      window.open(`https://wa.me/919711146547?text=${waText}`, "_blank");
    } catch (error) {
      console.error("EmailJS error:", error);
      toast.error(`Could not send. Please call ${CLINIC_PHONE}.`);
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    // Reset state after a delay to allow for closing animation
    setTimeout(() => {
        setData({ name: "", email: "", message: "" });
        setDone(false);
        setSubmitting(false);
    }, 300);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-end md:p-6" data-testid="ai-chat-modal">
      <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full md:w-[420px] bg-bgmain border border-cream rounded-t-3xl md:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-cream bg-gradient-to-r from-beige to-bgmain">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-ink text-bgmain flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="font-serif text-ink text-lg leading-tight">Quick Message</div>
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-mocha font-semibold">We'll reply via Email/WhatsApp</div>
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-full hover:bg-cream" data-testid="ai-chat-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 px-5 py-4">
          {done ? (
            <div className="text-center py-12 flex flex-col items-center justify-center h-full">
              <div className="w-16 h-16 mx-auto rounded-full bg-ink text-bgmain flex items-center justify-center mb-5"><Check size={28} /></div>
              <h3 className="font-serif text-2xl text-ink mb-2">Message Received!</h3>
              <p className="text-inkmuted text-sm">We'll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
               <p className="text-sm text-inkmuted mb-4">Have a question? Drop us a line and we'll get back to you as soon as possible.</p>
              <input value={data.name} onChange={(e) => setData(d => ({...d, name: e.target.value}))} placeholder="Your Name (Optional)" className="w-full px-4 py-2.5 rounded-lg border border-cream bg-white text-sm" />
              <input type="email" value={data.email} onChange={(e) => setData(d => ({...d, email: e.target.value}))} placeholder="Your Email (Optional)" className="w-full px-4 py-2.5 rounded-lg border border-cream bg-white text-sm" />
              <textarea
                required
                value={data.message}
                onChange={(e) => setData(d => ({...d, message: e.target.value}))}
                placeholder="Your message..."
                rows={5}
                className="w-full px-4 py-2.5 rounded-lg border border-cream bg-white resize-none text-sm"
                data-testid="ai-chat-input"
              />
              <button type="submit" disabled={submitting} className="w-full liquid-glass-dark flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-chat-send">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                Send Message
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
